<script setup>
import { computed } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import FrontLayout from '@/Layouts/FrontLayout.vue';
import CarCard from '@/Components/Front/CarCard.vue';

const page = usePage();

const props = defineProps({
    kecamatan: Object,
    artikel: Object,
    featuredArtikel: Array,
    setting: Object,
    mobils: Array,
    kecamatanFooter: Array,
    seoData: Object,
});

const wa = computed(() => props.setting?.setting_no_wa || '6281234567890');
const waUrl = computed(() => `https://wa.me/${wa.value}?text=Halo,%20saya%20ingin%20sewa%20mobil`);
const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

// JSON-LD Schema
const jsonLd = computed(() => {
    const appUrl = page.props.app_url || window.location.origin;
    const currentUrl = props.seoData?.canonical || window.location.href;
    const setting = props.setting || {};
    const logoUrl = setting.setting_logo ? `/storage/logo/${setting.setting_logo}` : '/front/img/logo.png';
    const kecamatanName = props.kecamatan?.nama_kecamatan || 'Banyuwangi';

    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Service",
                "name": `Sewa Mobil ${kecamatanName}`,
                "description": props.seoData?.description || `Layanan rental mobil terbaik di wilayah ${kecamatanName}, Banyuwangi.`,
                "provider": {
                    "@type": "LocalBusiness",
                    "name": setting.setting_title || 'Gotransport Banyuwangi',
                    "image": `${appUrl}${logoUrl}`,
                    "telephone": setting.setting_no_telp || setting.setting_no_wa,
                    "address": {
                        "@type": "PostalAddress",
                        "streetAddress": setting.setting_alamat,
                        "addressLocality": "Banyuwangi",
                        "addressRegion": "Jawa Timur",
                        "addressCountry": "ID"
                    }
                },
                "areaServed": {
                    "@type": "AdministrativeArea",
                    "name": kecamatanName,
                    "address": {
                         "@type": "PostalAddress",
                         "addressLocality": kecamatanName,
                         "addressRegion": "Banyuwangi"
                    }
                },
                "url": currentUrl
            },
            {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { 
                        "@type": "ListItem", 
                        "position": 1, 
                        "name": "Beranda", 
                        "item": appUrl 
                    },
                    { 
                        "@type": "ListItem", 
                        "position": 2, 
                        "name": "Area Layanan", 
                        "item": `${appUrl}/area-layanan` 
                    },
                    { 
                        "@type": "ListItem", 
                        "position": 3, 
                        "name": kecamatanName, 
                        "item": currentUrl 
                    }
                ]
            }
        ]
    };
});

defineOptions({
    layout: FrontLayout,
});
</script>

<template>
    <Head>
        <!-- SEO Meta Tags handled by FrontLayout for Title/Desc -->
        <!-- Additional tags if needed can go here, e.g. keywords if supported or strict overrides -->
        <meta v-if="props.seoData?.keywords" name="keywords" :content="props.seoData?.keywords" />
        <link v-if="props.seoData?.canonical" rel="canonical" :href="props.seoData?.canonical" />
        
        <!-- Open Graph -->
        <meta v-if="props.seoData?.og_title" property="og:title" :content="props.seoData?.og_title" />
        <meta v-if="props.seoData?.og_description" property="og:description" :content="props.seoData?.og_description" />
        <meta v-if="props.seoData?.og_image" property="og:image" :content="props.seoData?.og_image" />
        <meta v-if="props.seoData?.og_url" property="og:url" :content="props.seoData?.og_url" />
        <meta v-if="props.seoData?.og_type" property="og:type" :content="props.seoData?.og_type" />
        <meta property="og:site_name" content="Gotransport Banyuwangi" />
        <component :is="'script'" type="application/ld+json">{{ JSON.stringify(jsonLd) }}</component>
    </Head>

    <div class="font-sans antialiased text-gray-900 leading-normal tracking-wider bg-white dark:bg-gray-900 min-h-screen flex flex-col">
        <!-- Area Layanan Hero & Intro Section -->
        <section class="relative py-12 md:py-16 overflow-hidden">
            <!-- Standard Brand Gradient Background -->
            <div class="absolute inset-0 bg-gradient-to-br from-orange-600 via-orange-500 to-amber-400"></div>

            <!-- Decorative corner blob (White Overlay) -->
            <div class="absolute top-0 right-0 w-32 h-32 md:w-64 md:h-64 bg-white/10 rounded-bl-full pointer-events-none"></div>
            <div class="absolute bottom-0 left-0 w-24 h-24 md:w-48 md:h-48 bg-white/10 rounded-tr-full pointer-events-none"></div>

            <div class="container mx-auto px-4 max-w-5xl relative z-10">
                <div class="text-center mb-8">
                    <!-- Breadcrumb -->
                    <div class="inline-flex flex-wrap justify-center items-center gap-x-2 gap-y-1 text-xs md:text-sm text-white/90 bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-2xl md:rounded-full border border-white/20 shadow-sm mb-4 max-w-full">
                        <Link href="/" class="hover:text-white transition-colors font-medium">Beranda</Link>
                        <span class="text-white/60">/</span>
                        <Link href="/area-layanan" class="hover:text-white transition-colors font-medium">Area Layanan</Link>
                        <span class="text-white/60">/</span>
                        <span class="text-white font-bold">{{ kecamatan?.nama_kecamatan }}</span>
                    </div>
                    
                    <!-- Title -->
                    <h1 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 drop-shadow-sm break-words">
                        Layanan Rental Mobil {{ kecamatan?.nama_kecamatan }}
                    </h1>
                    <p class="text-orange-50 font-medium text-lg text-shadow-sm px-2">
                        {{ setting?.setting_title || 'Gotransport' }} - Partner Terpercaya Anda
                    </p>
                </div>

                <!-- Description Box -->
                <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 mb-8 text-center relative overflow-hidden group hover:-translate-y-1 transition-transform duration-300">
                    <p class="text-gray-600 dark:text-gray-300 leading-relaxed relative z-10 break-words">
                        {{ kecamatan?.deskripsi_kecamatan || `Kecamatan ${kecamatan?.nama_kecamatan} adalah salah satu wilayah strategis di Kabupaten Banyuwangi yang terkenal dengan potensi pertanian dan wisata alamnya yang menawan. Terletak di bagian yang strategis, kecamatan ini menawarkan akses mudah ke berbagai destinasi menarik. Wilayah ${kecamatan?.nama_kecamatan} memiliki karakteristik geografis yang unik dan pelayanan transportasi kami siap menemani Anda menjelajahinya.` }}
                    </p>
                </div>

                <!-- Features Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Feature 1 -->
                    <div class="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm p-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-start gap-4 hover:bg-white dark:hover:bg-gray-700">
                        <div class="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-orange-50 dark:bg-gray-700 rounded-full">
                             <i class="fa fa-key text-xl text-orange-600 dark:text-orange-400"></i>
                        </div>
                        <div>
                            <h4 class="font-bold text-gray-900 dark:text-white">Lepas Kunci</h4>
                            <p class="text-sm text-gray-500 dark:text-gray-400">Bebas kemana saja</p>
                        </div>
                    </div>

                    <!-- Feature 2 -->
                    <div class="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm p-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-start gap-4 hover:bg-white dark:hover:bg-gray-700">
                        <div class="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-orange-50 dark:bg-gray-700 rounded-full">
                             <i class="fa fa-user-secret text-xl text-gray-800 dark:text-gray-200"></i>
                        </div>
                        <div>
                            <h4 class="font-bold text-gray-900 dark:text-white">Dengan Sopir</h4>
                            <p class="text-sm text-gray-500 dark:text-gray-400">Profesional & aman</p>
                        </div>
                    </div>

                    <!-- Feature 3 -->
                    <div class="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm p-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-start gap-4 hover:bg-white dark:hover:bg-gray-700">
                        <div class="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-orange-50 dark:bg-gray-700 rounded-full">
                             <i class="fa fa-bolt text-xl text-yellow-500"></i>
                        </div>
                        <div>
                            <h4 class="font-bold text-gray-900 dark:text-white">Booking Cepat</h4>
                            <p class="text-sm text-gray-500 dark:text-gray-400">Via WhatsApp</p>
                        </div>
                    </div>

                    <!-- Feature 4 -->
                    <div class="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm p-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-start gap-4 hover:bg-white dark:hover:bg-gray-700">
                        <div class="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-orange-50 dark:bg-gray-700 rounded-full">
                             <i class="fa fa-thumbs-up text-xl text-orange-600 dark:text-orange-400"></i>
                        </div>
                        <div>
                            <h4 class="font-bold text-gray-900 dark:text-white">Harga Terbaik</h4>
                            <p class="text-sm text-gray-500 dark:text-gray-400">Kompetitif & transparan</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Armada Section -->
        <section id="car" class="py-8 md:py-10 bg-slate-100 dark:bg-gray-900 border-t border-slate-200 dark:border-gray-800">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <!-- Section Header -->
                <div class="text-center mb-6 md:mb-8">
                    <span class="inline-block px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium mb-2">
                        Pilihan Terbaik
                    </span>
                    <h3 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        Harga Sewa Rental Mobil Banyuwangi
                    </h3>
                    <p class="text-gray-600 dark:text-gray-400 text-sm max-w-2xl mx-auto">
                        Temukan mobil impian Anda dengan harga terbaik dan pelayanan terpercaya
                    </p>
                </div>

                <div v-if="mobils && mobils.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                     <CarCard v-for="mobil in mobils" :key="mobil.id" :mobil="mobil" :setting="setting" />
                </div>
                
                <div v-else class="text-center py-12">
                    <p class="text-gray-500 dark:text-gray-400">Belum ada data mobil tersedia.</p>
                </div>
            </div>
        </section>

        <!-- Articles/Info Section -->
        <section v-if="featuredArtikel?.length" class="py-12 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
             <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                 <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Panduan & Informasi {{ kecamatan?.nama_kecamatan }}</h3>
                 <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                     <div v-for="art in featuredArtikel" :key="art.id" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow">
                         <div v-if="art.featured_image" class="bg-gray-200 dark:bg-gray-700 overflow-hidden">
                             <img :src="`/storage/${art.featured_image}`" :alt="art.judul" class="w-full h-auto hover:scale-105 transition-transform duration-300" loading="lazy" />
                         </div>
                         <div class="p-4">
                             <h4 class="font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">{{ art.judul }}</h4>
                             <p class="text-sm text-gray-500 dark:text-gray-400 line-clamp-3 mb-4">{{ art.konten?.replace(/<[^>]*>?/gm, '') }}</p>
                             <Link :href="`/area-layanan/${kecamatan.slug}/${art.slug}`" class="text-orange-600 dark:text-orange-400 text-sm font-bold hover:underline">Baca Selengkapnya</Link>
                         </div>
                     </div>
                 </div>
             </div>
        </section>

        <!-- Map Section -->
        <section v-if="setting?.setting_map" class="h-[400px] w-full bg-gray-200 overflow-hidden relative">
             <div class="w-full h-full [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:border-0" v-html="setting.setting_map"></div>
        </section>

        <!-- Other Areas -->
        <section v-if="kecamatanFooter?.length" class="py-16 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="text-center mb-10">
                    <span class="text-orange-600 dark:text-orange-400 font-bold tracking-wider text-sm uppercase mb-2 block">Jangkauan Luas</span>
                    <h3 class="text-3xl font-bold text-gray-900 dark:text-white">Area Layanan Lainnya</h3>
                    <p class="text-gray-500 dark:text-gray-400 mt-2">Temukan layanan rental mobil terbaik di berbagai wilayah Banyuwangi lainnya</p>
                </div>
                
                <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <Link v-for="kec in kecamatanFooter" :key="kec.id" :href="`/area-layanan/${kec.slug}`" 
                          class="group bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-orange-300 dark:hover:border-orange-500 transition-all duration-300 flex items-center gap-3">
                        <div class="w-10 h-10 rounded-full bg-orange-50 dark:bg-gray-700 flex items-center justify-center text-orange-500 dark:text-orange-400 group-hover:bg-orange-500 group-hover:text-white transition-colors duration-300">
                            <i class="fa fa-map-marker text-lg"></i>
                        </div>
                        <span class="font-semibold text-gray-700 dark:text-gray-200 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">{{ kec.nama_kecamatan }}</span>
                        <i class="fa fa-chevron-right text-xs text-gray-300 dark:text-gray-600 ml-auto group-hover:text-orange-400"></i>
                    </Link>
                </div>
            </div>
        </section>
    </div>
</template>
